import React, { FC } from 'react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { StepIndicator } from '@quantum/ui-atoms';
import { Sender } from '@xstate/react/lib/types';
import { RenderSwitch } from '@/components/questionnaire/render-switch';
import { Question, QuestionnaireEventType, QuestionType } from '@/machines/types';
import { consentPage } from '@/constants/question-type';
import { QuestionHeader } from '@quantum/ui-molecules';
import * as styles from './styles.module.scss';

interface AssessmentLayoutProps {
  displayBackButton?: boolean;
  isPreviousSingleSelect?: boolean;
  isBack?: boolean;
  previousQuestionsLength?: number;
  totalQuestions?: number;
  currentQuestion?: Question;
  handleBackHeader?: () => void;
  sendEvent: Sender<QuestionnaireEventType>;
}

const variants = (isBack: boolean, currentDelay: number, previousDelay: number): Variants => ({
  initial: {
    opacity: 0,
    x: isBack ? '-100vw' : '100vw',
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      x: { delay: previousDelay, duration: 0.4 },
      opacity: { delay: previousDelay, duration: 0.4 },
    },
  },
  exit: {
    opacity: 0,
    x: isBack ? '20vw' : '-20vw',
    transition: {
      x: { delay: currentDelay, duration: 0.4 },
      opacity: { delay: currentDelay, duration: 0.4 },
    },
  },
});

export const AssessmentLayout: FC<AssessmentLayoutProps> = ({
  displayBackButton = false,
  isPreviousSingleSelect = false,
  isBack = false,
  previousQuestionsLength = 0,
  totalQuestions = 0,
  currentQuestion,
  handleBackHeader = () => {},
  sendEvent,
}) => (
  <QuestionHeader handleBackEvent={displayBackButton ? handleBackHeader : undefined} href='/'>
    <StepIndicator
      steps={totalQuestions}
      activeStep={previousQuestionsLength}
      displayBackground={previousQuestionsLength !== 0}
    />
    {currentQuestion && (
      <div className={styles.container}>
        <AnimatePresence>
          <motion.div
            key={currentQuestion.id}
            variants={variants(
              isBack,
              !currentQuestion.html &&
                !currentQuestion.interstitial &&
                (currentQuestion.questionTypeString === QuestionType.singleSelect ||
                  currentQuestion.key === 'selfDescribedGender') &&
                !isBack
                ? 0.8
                : 0,
              isPreviousSingleSelect ? 0.8 : 0,
            )}
            initial='initial'
            animate='animate'
            exit='exit'
            className={`${styles.root} ${currentQuestion?.key === consentPage ? styles.consentPage : ''}`}
          >
            <div className={styles.questionItem}>
              <RenderSwitch question={currentQuestion} sendEvent={sendEvent} />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    )}
  </QuestionHeader>
);
